import {
  skipToken,
  queryOptions as tanstackQueryOptions,
  useQuery,
} from '@tanstack/react-query';
import type { ApiResponse } from '@/types/api-types';
import type { QueryOptions } from '@/types/tanstack-query-options';
import type { paths } from '~/generated/inis/api-schema';
import {
  type PatientCardResponse,
  parsePatientCard,
} from '@/components/patient-card/patient-card-schema';
import { INIS_NAME } from '@/consts/system-name';
import { DataNotFoundError } from '@/lib/custom-api-errors';
import { tryCatch } from '@/lib/try-catch';
import { inisApi } from '@/services';

const PATH_VAR = '{mrn}';
const API_PATH = `/v1/patient/queryPatientCard/${PATH_VAR}` as const;
const QUERY_KEY = '/v1/patient/queryPatientCard' as const;

type APIResponse = ApiResponse<paths, typeof API_PATH>;

const queryPatientCard = async (mrn: string, signal: AbortSignal) => {
  const { data: response, error } = await tryCatch(
    inisApi
      .get<APIResponse>(API_PATH.replace(PATH_VAR, mrn), { signal })
      .then(({ data }) => data),
  );

  if (error instanceof DataNotFoundError) {
    return null;
  }

  if (error) throw error;

  return parsePatientCard(response.data);
};

const getQueryPatientCardOptions = (
  mrn?: string,
  options?: QueryOptions<PatientCardResponse | null>,
) =>
  tanstackQueryOptions({
    queryKey: [QUERY_KEY, INIS_NAME, mrn] as const,
    queryFn: mrn ? ({ signal }) => queryPatientCard(mrn, signal) : skipToken,
    staleTime: 5 * 60 * 1000,
    ...options,
  });

export const useQueryPatientCard = (
  mrn?: string,
  options?: QueryOptions<PatientCardResponse | null>,
) => useQuery(getQueryPatientCardOptions(mrn, options));
